import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faClock,
  faSearch,
  faPlay,
  faExclamationTriangle,
  faSpinner,
  faArrowRight,
  faSitemap,
  faCoins,
  faTags,
  faArrowLeft,
  faTrash,
  faExternalLinkAlt,
  faBan,
  faUndo,
  faLock,
  faLink,
  faLinkSlash
} from '@fortawesome/free-solid-svg-icons';
import { formatDistanceToNow } from 'date-fns';
import Cookies from 'js-cookie';
import MainLayout from '../Layout/MainLayout';
import { useAuth } from '../../context/AuthContext';

const EnhancedThreadsList = () => {
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('workflow'); // 'workflow' or 'regular'
  const [statusFilter, setStatusFilter] = useState('all');
  const [closedFilter, setClosedFilter] = useState('all'); // 'all' | 'hide' | 'only'
  const [poFilter, setPoFilter] = useState('all'); // 'all' | 'linked' | 'no_po' — admin only
  const [dateFilter, setDateFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agentId'); // Get agentId from URL for back navigation
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Handle back navigation
  const handleBack = () => {
    if (agentId) {
      navigate(`/agents/${agentId}`);
    } else {
      navigate('/agents');
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    filterAndSortSessions();
  }, [sessions, searchTerm, activeTab, statusFilter, closedFilter, poFilter, dateFilter, fromDate, toDate]);

  // Reset to first page whenever the filtered result set changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, statusFilter, closedFilter, poFilter, dateFilter, fromDate, toDate, itemsPerPage]);

  // Normalized bill status (shared by the badge and the status filter).
  // Prefer the real workflow_runs status (run_status); the legacy
  // workflow_executions status is stale (stuck at awaiting_user).
  const getSessionStatus = (session) => {
    if (session.session_status === 'draft') return 'draft';
    const s = session.run_status || session.latest_execution?.status || session.session_status || 'active';
    return s === 'error' ? 'failed' : s;
  };

  const loadSessions = async () => {
    try {
      const token = Cookies.get('sankalvax_token');
      // Exclude bank workflows (16=Bank Statement, 17=Customer Payment, 18=Vendor Payment) from bill processing history
      // Bill Processing History: fetch EVERY bill run, not just the most recent 100.
      // workflow_id=14 keeps the list to bills — without it the higher limit would pull
      // in workflows 12/15/16 (578 non-bill sessions) that this page has no filter for.
      const response = await fetch('/api/chat/sessions?limit=1000&workflow_id=14', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      console.log('📋 Loaded sessions:', data.sessions?.length, 'total');
      console.log('📋 Workflow sessions:', data.sessions?.filter(s => s.is_workflow_chat).length);
      console.log('📋 Draft sessions:', data.sessions?.filter(s => s.session_status === 'draft'));
      setSessions(data.sessions || []);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortSessions = () => {
    let filtered = sessions;

    // Filter by chat type
    if (activeTab === 'workflow') {
      filtered = filtered.filter(session => session.is_workflow_chat);
    } else {
      filtered = filtered.filter(session => !session.is_workflow_chat);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(session =>
        (session.session_name && session.session_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (session.workflow_name && session.workflow_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(session => getSessionStatus(session) === statusFilter);
    }

    // Filter by closed flag ('all' shows everything, 'hide' drops closed, 'only' keeps closed)
    if (closedFilter === 'hide') {
      filtered = filtered.filter(session => !session.is_closed);
    } else if (closedFilter === 'only') {
      filtered = filtered.filter(session => session.is_closed);
    }

    // Filter by PO linkage (admin only). Sessions with no workflow run have po_linked
    // null and are excluded from both explicit choices, matching the "-" they render.
    if (isAdmin && poFilter !== 'all') {
      filtered = filtered.filter(session =>
        poFilter === 'linked' ? session.po_linked === true : session.po_linked === false
      );
    }

    // Filter by date range (based on last activity)
    if (dateFilter !== 'all') {
      const now = Date.now();
      const days = { today: 1, week: 7, month: 30 }[dateFilter];
      if (days) {
        const cutoff = now - days * 24 * 60 * 60 * 1000;
        filtered = filtered.filter(session => {
          const d = new Date(session.updated_at || session.created_at).getTime();
          return !isNaN(d) && d >= cutoff;
        });
      }
    }

    // Filter by explicit From/To dates (inclusive). Empty inputs are ignored, so
    // this has no effect unless the user picks a date.
    if (fromDate || toDate) {
      const fromTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
      const toTs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;
      filtered = filtered.filter(session => {
        const d = new Date(session.updated_at || session.created_at).getTime();
        if (isNaN(d)) return false;
        if (fromTs !== null && d < fromTs) return false;
        if (toTs !== null && d > toTs) return false;
        return true;
      });
    }

    // Sort by created_at (newest first) - keeps order stable when workflows complete
    filtered = filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setFilteredSessions(filtered);
  };

  const continueChat = (session) => {
    if (session.is_workflow_chat && session.workflow_id) {
      // Workflow chat - redirect to SankalvaX Chat with workflow context (V2 by default)
      navigate(`/chat/${session.session_id}?workflow=${session.workflow_id}&workflowName=${encodeURIComponent(session.workflow_name || 'Workflow')}&mode=workflow&version=v2`);
    } else {
      // Regular chat - redirect to SankalvaX Chat with session ID
      navigate(`/chat/${session.session_id}`);
    }
  };

  const [deletingId, setDeletingId] = useState(null);

  // ADMIN-ONLY hard delete: purges the run + all its data. Backend also enforces admin.
  const handleDeleteSession = async (session, e) => {
    if (e) e.stopPropagation();
    const label = session.session_name || session.workflow_name || session.session_id;
    if (!window.confirm(`Delete this bill and EVERYTHING about its workflow run?\n\n"${label}"\n\nThis removes the run, steps, context, and chat history permanently. This cannot be undone.`)) {
      return;
    }
    try {
      setDeletingId(session.session_id);
      const token = Cookies.get('sankalvax_token');
      const resp = await fetch(`/api/chat/sessions/${session.session_id}/purge`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Delete failed (${resp.status})`);
      }
      // remove from the list immediately, then reload for consistency
      setSessions(prev => prev.filter(s => s.session_id !== session.session_id));
      loadSessions();
    } catch (err) {
      console.error('Delete failed:', err);
      window.alert(`Could not delete: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  // ---- Close / Reopen a bill (bookkeeping flag; does not touch the run or NetSuite) ----
  const CLOSE_REASONS = [
    { key: 'po_closed', label: 'PO closed in system' },
    { key: 'reuploaded_pdf', label: 'Re-uploaded PDF' },
    { key: 'received_from_email', label: 'Received from email again' },
    { key: 'duplicate_bill', label: 'Duplicate bill' },
    { key: 'other', label: 'Other' },
  ];
  const [closeModalSession, setCloseModalSession] = useState(null); // session being closed
  const [closeReason, setCloseReason] = useState('po_closed');
  const [closeNote, setCloseNote] = useState('');
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  const [reopeningId, setReopeningId] = useState(null);

  const openCloseModal = (session, e) => {
    if (e) e.stopPropagation();
    setCloseModalSession(session);
    setCloseReason('po_closed');
    setCloseNote('');
  };

  const submitClose = async () => {
    if (!closeModalSession) return;
    if (closeReason === 'other' && !closeNote.trim()) {
      window.alert('Please enter a note when the reason is "Other".');
      return;
    }
    try {
      setCloseSubmitting(true);
      const token = Cookies.get('sankalvax_token');
      const resp = await fetch(`/api/chat/sessions/${closeModalSession.session_id}/close`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: closeReason, note: closeNote.trim() || null })
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Close failed (${resp.status})`);
      }
      setCloseModalSession(null);
      loadSessions();
    } catch (err) {
      console.error('Close failed:', err);
      window.alert(`Could not close bill: ${err.message}`);
    } finally {
      setCloseSubmitting(false);
    }
  };

  const handleReopen = async (session, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Reopen this bill?\n\n"${session.session_name || session.workflow_name || session.session_id}"\n\nIt will no longer be marked as closed.`)) {
      return;
    }
    try {
      setReopeningId(session.session_id);
      const token = Cookies.get('sankalvax_token');
      const resp = await fetch(`/api/chat/sessions/${session.session_id}/reopen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Reopen failed (${resp.status})`);
      }
      loadSessions();
    } catch (err) {
      console.error('Reopen failed:', err);
      window.alert(`Could not reopen bill: ${err.message}`);
    } finally {
      setReopeningId(null);
    }
  };

  const closeReasonLabel = (key) => (CLOSE_REASONS.find(r => r.key === key) || {}).label || key;

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`;
  };

  // Derive a bill status badge from the session's draft flag + latest execution.
  const getStatusBadge = (session) => {
    const status = getSessionStatus(session);
    const cfg = {
      completed:     { label: 'Completed',     className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-green-300 dark:border-green-700' },
      running:       { label: 'Running',       className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-300 dark:border-blue-700' },
      awaiting_user: { label: 'Awaiting User', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700' },
      failed:        { label: 'Failed',        className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-300 dark:border-red-700' },
      error:         { label: 'Failed',        className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-300 dark:border-red-700' },
      draft:         { label: 'Draft',         className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 border-orange-300 dark:border-orange-700' },
      active:        { label: 'In Progress',   className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600' },
    };
    const c = cfg[status] || cfg.active;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${c.className}`}>
        {c.label}
      </span>
    );
  };

  const workflowSessions = sessions.filter(s => s.is_workflow_chat);
  const regularSessions = sessions.filter(s => !s.is_workflow_chat);

  // Pagination (clamp page in case the list shrank)
  const totalPages = Math.max(1, Math.ceil(filteredSessions.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * itemsPerPage;
  const paginatedSessions = filteredSessions.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <MainLayout title="Bill Processing History">
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-4xl text-blue-500 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading your conversations...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Bill Processing History">
      <div className="w-full p-6 space-y-6">
        
        {/* Back Button */}
        <div className="flex items-center mb-4">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-600 dark:text-gray-400"
            title={agentId ? "Back to Agent" : "Back to Agents"}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span className="text-sm">{agentId ? "Back to Agent" : "Back to Agents"}</span>
          </button>
        </div>

        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-3xl mb-6 shadow-xl">
            <FontAwesomeIcon icon={faComments} className="text-white text-2xl" />
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-4">
            Bill Processing History
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-xl max-w-2xl mx-auto">
            Review your processed bills and continue any that need your attention
          </p>
        </div>

        {/* Tabs Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center space-x-2 px-4 py-3 bg-gray-100 dark:bg-gray-800 rounded-xl">
            <FontAwesomeIcon icon={faSitemap} className="text-blue-600 dark:text-blue-400 text-sm" />
            <span className="font-medium text-gray-700 dark:text-gray-300">Bills</span>
            <span className="ml-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs rounded-full">
              {workflowSessions.length}
            </span>
          </div>

          {/* Filters + Search */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="awaiting_user">Awaiting User</option>
              <option value="running">Running</option>
              <option value="failed">Failed</option>
              <option value="draft">Draft</option>
            </select>

            {/* Closed filter */}
            <select
              value={closedFilter}
              onChange={(e) => setClosedFilter(e.target.value)}
              title="Show or hide closed bills"
              className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
            >
              <option value="all">All (incl. closed)</option>
              <option value="hide">Hide closed</option>
              <option value="only">Only closed</option>
            </select>

            {/* PO linkage filter (admin only) */}
            {isAdmin && (
              <select
                value={poFilter}
                onChange={(e) => setPoFilter(e.target.value)}
                title="Filter by PO linkage"
                className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
              >
                <option value="all">PO: All</option>
                <option value="linked">PO Linked</option>
                <option value="no_po">No PO</option>
              </select>
            )}

            {/* Date filter */}
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
            >
              <option value="all">All Time</option>
              <option value="today">Last 24 Hours</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>

            {/* From / To date range */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
                title="From date"
                className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
              />
              <span className="text-gray-400 text-sm">to</span>
              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
                title="To date"
                className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
              />
              {(fromDate || toDate) && (
                <button
                  onClick={() => { setFromDate(''); setToDate(''); }}
                  title="Clear date range"
                  className="px-2 py-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-72">
              <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search bills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
              />
            </div>
          </div>
        </div>

        {/* Sessions Grid */}
        {filteredSessions.length === 0 ? (
          <div className="text-center py-16">
            <FontAwesomeIcon
              icon={faSitemap}
              className="text-6xl text-gray-300 dark:text-gray-600 mb-6"
            />
            <h3 className="text-2xl font-semibold text-gray-500 dark:text-gray-400 mb-3">
              {searchTerm ? 'No matches found' : 'No bills yet'}
            </h3>
            <p className="text-gray-400 dark:text-gray-500 mb-6">
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Processed bills will appear here'
              }
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-12">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Bill</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Duplicate</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Action</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">View Bill</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Close</th>
                    {isAdmin && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">PO</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedSessions.map((session, index) => (
                    <tr
                      key={session.session_id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors ${session.is_closed ? 'opacity-60 bg-gray-50/60 dark:bg-gray-800/40' : ''}`}
                    >
                      {/* Row number */}
                      <td className="px-4 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                        {startIndex + index + 1}
                      </td>

                      {/* Bill name + icon */}
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600">
                            <FontAwesomeIcon icon={faSitemap} className="text-white text-sm" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <div className={`text-sm font-semibold text-gray-900 dark:text-white truncate max-w-xs ${session.is_closed ? 'line-through text-gray-500 dark:text-gray-400' : ''}`}>
                                {(session.session_name || session.workflow_name || `Workflow ${session.workflow_id}`).replace(' (Duplicate)', '')}
                              </div>
                              {session.is_closed && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 flex-shrink-0"
                                  title={`Closed: ${closeReasonLabel(session.closed_reason)}${session.closed_note ? ` — ${session.closed_note}` : ''}${session.closed_at ? ` (${formatDate(session.closed_at)})` : ''}`}
                                >
                                  <FontAwesomeIcon icon={faLock} className="text-[9px]" />
                                  Closed
                                </span>
                              )}
                            </div>
                            {session.bill_invoice_number && session.bill_invoice_number !== 'null' && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                Invoice #{session.bill_invoice_number}
                              </div>
                            )}
                            {session.partial_ir && (
                              <div
                                className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300 dark:border-amber-700"
                                title={session.partial_ir_remark || 'Invoice quantity exceeds the received (IR) quantity'}
                              >
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-[9px]" />
                                Partial IR
                              </div>
                            )}
                            {session.latest_execution?.error_message && (
                              <div className="text-xs text-red-600 dark:text-red-400 truncate max-w-xs">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                                {session.latest_execution.error_message.substring(0, 60)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Amount */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {session.bill_total_amount && session.bill_total_amount !== 'null' && !isNaN(parseFloat(session.bill_total_amount)) ? (
                          <span className="inline-flex items-center text-sm font-bold text-green-700 dark:text-green-300">
                            <FontAwesomeIcon icon={faCoins} className="text-xs mr-1.5" />
                            {(session.bill_currency && session.bill_currency !== 'null' && session.bill_currency !== 'None') ? session.bill_currency : '$'}{' '}
                            {parseFloat(session.bill_total_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getStatusBadge(session)}
                      </td>

                      {/* Duplicate flag (duplicate runs are named "... (Duplicate)" by the backend guard) */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {session.session_name && session.session_name.includes('(Duplicate)') ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1 text-xs" />
                            Duplicate
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>

                      {/* Date */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(session.updated_at || session.created_at)}
                      </td>

                      {/* Action */}
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <div className="inline-flex items-center space-x-2">
                          <button
                            onClick={() => continueChat(session)}
                            disabled={session.is_closed}
                            title={session.is_closed ? 'This bill is closed. Reopen it to continue.' : undefined}
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-semibold shadow disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-blue-700"
                          >
                            <span>Continue</span>
                            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={(e) => handleDeleteSession(session, e)}
                              disabled={deletingId === session.session_id}
                              title="Delete this bill and all its workflow data (admin only)"
                              className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all duration-200 disabled:opacity-50"
                            >
                              <FontAwesomeIcon icon={deletingId === session.session_id ? faSpinner : faTrash} className={deletingId === session.session_id ? 'animate-spin text-xs' : 'text-xs'} />
                            </button>
                          )}
                        </div>
                      </td>

                      {/* View Bill (opens the created NetSuite bill in a new tab; only for completed bills that produced a NetSuite bill) */}
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {getSessionStatus(session) === 'completed' && session.netsuite_bill_url ? (
                          <a
                            href={session.netsuite_bill_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={session.is_duplicate ? 'View the existing bill in NetSuite' : 'View Bill in NetSuite'}
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 text-sm font-semibold shadow"
                          >
                            <span>View Bill</span>
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                          </a>
                        ) : session.duplicate_type === 'draft' && session.duplicate_draft_session_id ? (
                          <button
                            onClick={() => navigate(`/chat/${session.duplicate_draft_session_id}?workflow=14&workflowName=${encodeURIComponent('Bill Processing')}&mode=workflow&version=v2`)}
                            title="Open the existing draft bill"
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg hover:from-red-700 hover:to-rose-700 transition-all duration-200 text-sm font-semibold shadow"
                          >
                            <span>View Draft</span>
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>

                      {/* Close / Reopen (manual bookkeeping flag; not allowed on completed bills) */}
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {session.is_closed ? (
                          <button
                            onClick={(e) => handleReopen(session, e)}
                            disabled={reopeningId === session.session_id}
                            title="Reopen this bill"
                            className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50"
                          >
                            <FontAwesomeIcon icon={reopeningId === session.session_id ? faSpinner : faUndo} className={reopeningId === session.session_id ? 'animate-spin text-xs' : 'text-xs'} />
                            <span>Reopen</span>
                          </button>
                        ) : getSessionStatus(session) === 'completed' ? (
                          <span className="text-sm text-gray-400 dark:text-gray-500" title="Completed bills cannot be closed">—</span>
                        ) : (
                          <button
                            onClick={(e) => openCloseModal(session, e)}
                            title="Close this bill"
                            className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-semibold text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all duration-200"
                          >
                            <FontAwesomeIcon icon={faBan} className="text-xs" />
                            <span>Close</span>
                          </button>
                        )}
                      </td>

                      {/* PO linkage (admin only). "Linked" = a NetSuite PO was actually
                          matched for this run; "-" = session has no workflow run. */}
                      {isAdmin && (
                        <td className="px-4 py-4 whitespace-nowrap">
                          {session.po_linked === true ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                              <FontAwesomeIcon icon={faLink} className="mr-1 text-xs" />
                              PO Linked
                            </span>
                          ) : session.po_linked === false ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                              <FontAwesomeIcon icon={faLinkSlash} className="mr-1 text-xs" />
                              No PO
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination bar */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
              {/* Range + page size */}
              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <span>
                  Showing <span className="font-semibold text-gray-900 dark:text-white">{filteredSessions.length === 0 ? 0 : startIndex + 1}</span>
                  {' '}–{' '}
                  <span className="font-semibold text-gray-900 dark:text-white">{Math.min(startIndex + itemsPerPage, filteredSessions.length)}</span>
                  {' '}of{' '}
                  <span className="font-semibold text-gray-900 dark:text-white">{filteredSessions.length}</span>
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white text-sm"
                >
                  {[10, 25, 50, 100].map(n => (
                    <option key={n} value={n}>{n} / page</option>
                  ))}
                </select>
              </div>

              {/* Page controls */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={safePage === 1}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  «
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Prev
                </button>

                {/* Numbered pages (windowed around current) */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                  .reduce((acc, p) => {
                    if (acc.length && p - acc[acc.length - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => p === '…' ? (
                    <span key={`e${i}`} className="px-2 text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        p === safePage
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={safePage === totalPages}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  »
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Stats */}
        <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {workflowSessions.length}
              </div>
              <div className="text-gray-600 dark:text-gray-400">Total Bills</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {sessions.reduce((sum, s) => sum + (s.total_tokens_used || 0), 0).toLocaleString()}
              </div>
              <div className="text-gray-600 dark:text-gray-400">Total Tokens</div>
            </div>
          </div>
        </div>
      </div>

      {/* Close Bill modal */}
      {closeModalSession && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !closeSubmitting && setCloseModalSession(null)}
        >
          <div
            className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                <FontAwesomeIcon icon={faBan} />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Close Bill</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {(closeModalSession.session_name || closeModalSession.workflow_name || closeModalSession.session_id).replace(' (Duplicate)', '')}
                </p>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Reason</label>
                <select
                  value={closeReason}
                  onChange={(e) => setCloseReason(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white text-sm"
                >
                  {CLOSE_REASONS.map(r => (
                    <option key={r.key} value={r.key}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Note {closeReason === 'other' ? <span className="text-red-500">*</span> : <span className="text-gray-400 font-normal">(optional)</span>}
                </label>
                <textarea
                  value={closeNote}
                  onChange={(e) => setCloseNote(e.target.value)}
                  rows={3}
                  placeholder={closeReason === 'other' ? 'Describe the reason…' : 'Add any details (optional)…'}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white text-sm resize-none"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <button
                onClick={() => setCloseModalSession(null)}
                disabled={closeSubmitting}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitClose}
                disabled={closeSubmitting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={closeSubmitting ? faSpinner : faBan} className={closeSubmitting ? 'animate-spin text-xs' : 'text-xs'} />
                {closeSubmitting ? 'Closing…' : 'Close Bill'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default EnhancedThreadsList;